import classNames from 'classnames'
import React from 'react'
import { NavLink } from 'react-router-dom'

import { AlertIconWrapper } from '@ui/AlertIconWrapper/AlertIconWrapper'
import { Tab } from '@ui/components/Tabs/Tabs'
import { TourTarget } from '@ui/components/Tourtip/TourTarget'
import { TextColorType } from '@ui/design-system/colors/colors'
import { IconFCType } from '@ui/icons/types'

import styles from './TabItem.module.scss'

export type TabSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export const TAB_SIZE: Record<TabSize, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 56,
}
type IconSize = 14 | 16 | 18 | 20 | 24

const iconSizes: Record<TabSize, IconSize> = {
  xs: 14,
  sm: 16,
  md: 18,
  lg: 20,
  xl: 24,
}

const getIconSize = (size: TabSize): IconSize => {
  return iconSizes[size] || iconSizes.md
}

export interface TabItemProps<T = string> {
  tab: Tab<T> & { iconLeft?: IconFCType; iconRight?: IconFCType }
  isActive: boolean
  size?: TabSize
  alert?: boolean
  onClick: () => void
  replace?: boolean
}

export const TabItem = React.forwardRef<HTMLDivElement, TabItemProps<any>>((props, ref) => {
  const { tab, isActive, size = 'md', alert, onClick, replace } = props
  const { label, path, iconLeft: IconLeft, iconRight: IconRight, disabled } = tab
  const iconSize = getIconSize(size)

  const iconColor: TextColorType = isActive ? 'text-primary-med-em' : 'text-low-em'
  return (
    <NavLink
      to={path ?? ''}
      replace={replace}
      className={classNames(styles.tabItem, styles[`size-${size}`], {
        [styles.disabled]: disabled,
      })}
      role='tab'
      aria-selected={isActive}
      aria-disabled={disabled}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault()
          return
        }

        onClick()
      }}
    >
      <TourTarget target={tab.tourTarget}>
        <AlertIconWrapper spaced fullWidth ref={ref} isActive={alert}>
          <div className={styles.tabInner}>
            {IconLeft && <IconLeft color={iconColor} size={iconSize} />}
            <span className={classNames(styles.label, { [styles.active]: isActive })}>{label}</span>
            {IconRight && <IconRight color={iconColor} size={iconSize} />}
          </div>
        </AlertIconWrapper>
      </TourTarget>
    </NavLink>
  )
})

TabItem.displayName = 'TabItem'
